import { useMemo } from 'react';

import { useCareerSkillGraphBoardQuery } from 'core/graphql';

import { transformCareerSkillGraph } from './transform/transformCareerSkillGraph';

/**
 * Карта навыков одним запросом (ТЗ 0070).
 *
 * `cache-and-network`: карта меняется редко, но после «навык подтверждён» или
 * смены цели она обязана прийти свежей, а не показать вчерашнюю структуру из
 * кеша. Мгновенный кадр из кеша при этом остаётся — иначе переход на экран
 * начинается со спиннера на полсекунды.
 */
export const useCareerSkillGraph = (trackCode?: string | null) => {
  const { data, loading, error, refetch } = useCareerSkillGraphBoardQuery({
    variables: { trackCode: trackCode || null },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    // Без этого смена профессии не поднимает `loading`, и признак «показанная
    // карта ещё не та» ниже не срабатывает вовсе.
    notifyOnNetworkStatusChange: true,
  });

  const graph = useMemo(() => transformCareerSkillGraph(data), [data]);

  /**
   * 🔴 Пока пришедшая карта не та, которую попросили, экран считается
   * загружающимся. Apollo кеширует по переменным и на смену профессии отдаёт
   * прошлый ответ первым кадром — без этой проверки полсекунды видна ЧУЖАЯ
   * карта под новым названием в панели.
   *
   * 🔴 Условие включает `loading`. Сервер ОТКАТЫВАЕТСЯ на свою цель, когда код
   * неизвестен, — и тогда пришедший `trackCode` навсегда не равен
   * запрошенному. Без `loading` экран в этом случае грузился бы вечно, вместо
   * того чтобы честно показать карту цели.
   */
  const stale = loading && Boolean(trackCode) && graph !== null && graph.trackCode !== trackCode;

  return {
    graph: stale ? null : graph,
    // 🔴 «Загружается» — только ПЕРВАЯ загрузка. `cache-and-network` поднимает
    // `loading` на каждом фоновом обновлении, и экран, который слушает его
    // буквально, мигает спиннером поверх готовой карты при каждом возврате.
    loading: (loading && graph === null) || stale,
    error,
    refetch,
  };
};
